import type {
  BasketItem,
  BasketValidationResult,
} from "~/widgets/stores/basket-store";

/**
 * How many items must be resolved before this basket can be paid for.
 *
 * `VALIDATION_TRANSIENT` is the one code excluded. It means the availability
 * CHECK failed rather than the item being unavailable — any throw inside the
 * placement pre-check returns it — so blocking would refuse a basket over a
 * backend hiccup, and the next validate clears it.
 *
 * Keyed on the CODE, which since `UNKNOWN_ITEM_TYPE` moved to the `gone`
 * bucket is now equivalent to testing the bucket. Kept as the code because
 * that is the property this rule actually depends on — "retrying may fix it" —
 * and a future code added to `transient` should have to opt in here rather
 * than inherit a pass by bucket.
 *
 * `partial` does not block either, matching `basket-drawer.tsx` and the
 * widget's checkout page. An earlier draft of this branch DID block it, on the
 * grounds that this overlay replaces the drawer and offers no quantity
 * control, so an unfixable partial would otherwise be payable — and
 * `reserveBasketItems`, the server gate for it, is a no-op for any school with
 * `basket.reservationsEnabled` off. That reasoning was sound about the risk and
 * wrong about the remedy. Blocking produced dead ends instead of preventing
 * anything:
 *
 *   - `ensureBasketElement` was a hard no-op then, so the four surfaces that
 *     open this overlay without a `<baluu-basket>` had a refusal, no per-item
 *     UI, and no drawer to be sent to. The only exit was to leave.
 *   - An email-less validate counts the buyer's OWN abandoned hold among
 *     "others", so a buyer returning to their basket could be blocked by
 *     themselves.
 *   - On a ticketed line Reduce-to-N is withheld (it cannot reach the server's
 *     `requestedQuantity`), leaving Remove as the only move.
 *
 * The overselling risk is real but PRE-EXISTING and not this branch's to close
 * by refusing sales — the fix is the reservation gate, server-side, where it
 * can be right for every client. What this branch owed was making the verdict
 * VISIBLE, which it now is.
 *
 * Counts ITEMS, not verdicts: one item carries at most one `unavailability`.
 */
export function countBlockingItems(
  items: Pick<BasketItem, "isAvailable" | "unavailability">[],
): number {
  let count = 0;
  for (const item of items) {
    if (!item.unavailability) {
      // Legacy unstructured verdict, from an API older than the codes.
      if (item.isAvailable === false) count += 1;
      continue;
    }
    const { code } = item.unavailability;
    if (
      code === "VALIDATION_TRANSIENT" ||
      code === "SESSION_PARTIAL_CAPACITY" ||
      code === "PRODUCT_PARTIAL_STOCK"
    ) {
      continue;
    }
    count += 1;
  }
  return count;
}

/**
 * Ticket-visibility refusals that are only worth stamping when the asking
 * surface both KNOWS who the buyer is and can do something about the answer.
 *
 * `ValidateBasketItems` resolves the buyer's memberships and CRM tags only
 * when it is given a `buyerEmail` — without one, `buyerActivePlanIds` and
 * `buyerCustomerTags` stay empty and the visibility gate refuses every
 * members-only and tag-gated ticket by construction. The answer is not "you
 * are not a member", it is "nobody asked who you are".
 *
 * Left standing, that answer is a locked door: both are hard blockers in the
 * drawer, and the email that would clear them can only be typed in the
 * checkout overlay — behind the button the refusal disables.
 *
 * CLEARED rather than dropped. Dropping stops an anonymous validate from
 * inventing the refusal, but it also stops one from ever lifting a refusal
 * already stamped: a buyer who mistyped their email in the overlay, then took
 * the banner's advice and opened the drawer, found Checkout disabled by a
 * verdict the drawer could not revise, with no way back to an email field.
 * Clearing is accurate about what IS known — the server applies this gate only
 * to items that already passed every other check (`if (result.available &&
 * …)`), so everything judgeable without an identity did pass.
 */
const CLEARABLE_IDENTITY_VERDICTS = new Set([
  "TICKET_MEMBERS_ONLY",
  "TICKET_TAG_GATED",
]);

/**
 * Verdicts that turn on WHO IS BUYING — the clearable two, plus the trial
 * allowance. `TRIAL_LIMIT_REACHED` is identity-dependent in only one of its
 * two halves, which is why it is in THIS set and not the clearable one:
 *
 *   - with an email, the server counts the buyer's existing trials on the
 *     course — that half needs the identity, and only the overlay has it;
 *   - without one, the server still aggregates the trial items in the basket
 *     itself ("added the trial twice") — an anonymous validate that says so is
 *     right, and clearing it would invent an availability.
 *
 * So the overlay keeps the buyer for it (`identityBlockers`), but an
 * anonymous validate's own answer stands (`withoutBuyerDependentVerdicts`).
 */
const IDENTITY_BLOCKER_VERDICTS = new Set([
  ...CLEARABLE_IDENTITY_VERDICTS,
  "TRIAL_LIMIT_REACHED",
]);

export type IdentityBlockKind =
  | "members_only"
  | "tag_gated"
  | "trial_limit"
  | "mixed";

/**
 * Which blockers, if any, turn on WHO IS BUYING rather than on the
 * item — a members-only or tag-gated ticket the email did not satisfy, or a
 * trial ticket that email has already used up.
 *
 * These have to be handled apart from every other blocker, because the drawer
 * is the wrong place to send someone holding one. It has no email field, and
 * its revalidate is anonymous, so it would erase the very verdict that raised
 * the banner and show a basket with nothing wrong — the buyer then walks back
 * into checkout, retypes the same email, and is blocked again, having lost
 * their form on the way.
 *
 * The remedy for the first two lives in the checkout overlay: a different
 * email. The trial one is different — the email is right, the allowance is
 * used, and the only remedy is removing the ticket — so the overlay must SAY
 * so, and must not withhold the drawer where the removal happens.
 *
 * Returns the COUNT and the kind, not just a boolean, because the codes have
 * different remedies — "use your member's email" versus "check the address"
 * versus "remove the trial" — and a basket can hold more than one. `null`
 * means the blockers are not all identity ones, so the drawer is the right
 * place after all.
 */
export function identityBlockers(
  items: Pick<BasketItem, "isAvailable" | "unavailability">[],
): {
  count: number;
  kind: IdentityBlockKind;
  /**
   * Whether a different email could clear EVERY blocker. False as soon as a
   * used-up trial is among them: that one only goes by removing the ticket,
   * so the drawer must not be withheld even when the rest are
   * email-remediable — "check your email" with no way to the basket is a
   * dead end for the trial item.
   */
  emailRemediable: boolean;
} | null {
  const blockers = items.filter((i) => countBlockingItems([i]) > 0);
  if (blockers.length === 0) return null;

  const codes = blockers.map((i) => i.unavailability?.code);
  if (!codes.every((c) => c && IDENTITY_BLOCKER_VERDICTS.has(c))) return null;

  const allMembers = codes.every((c) => c === "TICKET_MEMBERS_ONLY");
  const allTags = codes.every((c) => c === "TICKET_TAG_GATED");
  const allTrial = codes.every((c) => c === "TRIAL_LIMIT_REACHED");
  return {
    count: blockers.length,
    kind: allMembers
      ? "members_only"
      : allTags
        ? "tag_gated"
        : allTrial
          ? "trial_limit"
          : "mixed",
    emailRemediable: codes.every((c) => c && CLEARABLE_IDENTITY_VERDICTS.has(c)),
  };
}

/**
 * The API origin, from the host page's hand-written `api-url` attribute.
 *
 * Both suffixes, because both appear in the wild and each breaks a different
 * half of the surface. Stripping only `/widget` left a bare trailing slash, so
 * `api-url="https://api.baluu.io/"` requested `//widget/basket/validate` and
 * 404'd; stripping only the slash left `api-url=".../widget"` posting to
 * `/widget/widget/app/processBasketPayment`, which 404'd after a payment had
 * already succeeded and left the basket uncleared.
 *
 * One function because it was seven copies. Two were fixed and five were not,
 * which is worse than uniform: a trailing slash then broke the school config,
 * discounts, checkout-session creation and payment processing while validation
 * carried on working.
 */
export function apiOrigin(apiUrl: string): string {
  return apiUrl.replace(/\/widget\/?$/, "").replace(/\/+$/, "");
}

/**
 * The item shape `/widget/basket/validate` is sent, built ONCE.
 *
 * The checkout overlay's two validate effects used to build this inline, and
 * those two copies are why rentals could never be checked out from an embed:
 * neither named `appointmentData`, so `ValidateBasketItems` could not read the
 * hire length and answered "This rental item is missing how long the hire is
 * for". The overlay then persisted that onto the item as `isAvailable: false`,
 * which survives in localStorage — so the item stayed dead, and re-adding
 * minted another flagged one.
 *
 * Keeping it in one place is the actual fix. A field-by-field payload silently
 * drops anything not named in it and there is no compiler error when a copy
 * falls behind — `timezone` went missing the same way once before. It lives
 * here rather than in the overlay because the basket drawer validates too, and
 * a second caller is exactly what would have re-created the two copies.
 * The widget keeps its own equivalent in one place for the same reason
 * (apps/widget/src/hooks/useBasketSync.ts).
 */
export function buildValidatePayload(items: BasketItem[]) {
  return items.map((item) => ({
    id: item.id,
    type: item.type,
    sessionId: item.sessionId,
    courseId: item.courseId,
    productId: item.productId,
    bundleTemplateId: item.bundleTemplateId,
    ticketSelections: item.ticketSelections,
    addOns: item.addOns,
    bookingType: item.bookingType,
    schoolId: item.schoolId,
    quantity: item.quantity,
    price: item.price,
    checkoutGroupId: item.checkoutGroupId,
    // WHEN the booking is for. The validator reads the hire length from
    // `appointmentData.fractions`, and `sessionDate` is what the
    // appointment/anytime pre-payment placement check keys off.
    sessionDate: item.sessionDate,
    appointmentData: item.appointmentData,
    // Exempts a waiting-list claimer's own offer hold from the capacity
    // subtraction before their email is known. Unset on this surface today;
    // named here so it cannot be the next `appointmentData`.
    offerRef: item.offerRef,
    // Products: the server resolves the postage option and the gift-wrap fee
    // from these (the widget sends them too, useBasketSync), and stamps the
    // surcharges the PaymentIntent will add.
    postageId: item.postageId,
    isGift: item.isGift,
  }));
}

/** Long enough for a slow phone on a bad connection, short enough that a
 *  stalled request doesn't hold the Checkout button hostage. Callers whose
 *  answer is a nicety rather than a gate should pass something shorter — see
 *  `timeoutMs`. */
const VALIDATE_TIMEOUT_MS = 15_000;


/**
 * `MEMBERSHIP_INACTIVE` is NOT in that set, and must not be cleared the same
 * way: the server emits it as a whole-basket refusal before any other check
 * runs (`membershipSubscriptionId` supplied, subscription lapsed), so nothing
 * about those items has been judged and `available: true` would be an
 * invention. It is dropped instead. Unreachable from this surface today in any
 * case — `buildValidatePayload` sends no `membershipSubscriptionId`.
 */
const DROP_ONLY_IDENTITY_VERDICTS = new Set(["MEMBERSHIP_INACTIVE"]);

/**
 * Strip the verdicts an email-less validate had no standing to reach, so its
 * answer says only what it actually established.
 */
function withoutBuyerDependentVerdicts(
  results: BasketValidationResult[],
): BasketValidationResult[] {
  return results
    .filter(
      (r) =>
        !r.unavailability || !DROP_ONLY_IDENTITY_VERDICTS.has(r.unavailability.code),
    )
    .map((r) => {
      if (!r.unavailability || !CLEARABLE_IDENTITY_VERDICTS.has(r.unavailability.code)) {
        return r;
      }
      return {
        ...r,
        available: true,
        message: undefined,
        unavailability: undefined,
      };
    });
}

type ValidateArgs = {
  apiUrl: string;
  schoolId: string;
  items: BasketItem[];
  /**
   * When present the server excludes THIS buyer's own holds from the "others"
   * sum, and refreshes their reservation TTLs as a heartbeat. Absent from the
   * drawer, which has no buyer.
   */
  buyerEmail?: string;
  /**
   * Whether this surface could DO anything with an identity refusal.
   *
   * True for the checkout overlay, which holds the email field; false for the
   * drawer, which does not. It is not the same question as whether an email
   * was sent, and both have to hold — which is why keying only on the email
   * broke twice:
   *
   *   - no email, so the server refuses every gated ticket by construction:
   *     the verdict is meaningless and must not be stamped anywhere;
   *   - email sent to the DRAWER, so the verdict is real but unactionable
   *     there: stamping it disables the Checkout button that is the only way
   *     back to the field which would change it. A mistyped address then
   *     locks the buyer out until they reload or remove the item.
   *
   * So the overlay passes true for BOTH its validates — its email-less mount
   * validate is still filtered, by the missing email — and the drawer passes
   * false regardless of what it knows.
   */
  canActOnIdentityVerdicts: boolean;
  /**
   * How long to wait before aborting, defaulting to `VALIDATE_TIMEOUT_MS`.
   *
   * Worth overriding because the drawer hands its `isValidating` to
   * `BasketDrawer`, which disables Checkout while it is true — so on that
   * surface the timeout is also how long a bad connection can hold the button
   * shut. The overlay wants the patient default: its answer decides whether
   * Pay is allowed at all.
   */
  timeoutMs?: number;
};

/**
 * Validate a basket against the server. Returns the per-item results, or
 * `null` when the server refused the request — callers treat that as
 * "no verdict", not as "everything is unavailable".
 *
 * Rejects on a network failure, like `fetch` itself. Every caller here is
 * non-blocking: the checkout-session creation re-validates server-side
 * anyway, so failing to reach this endpoint must never strand a buyer.
 */
export async function validateBasketItems({
  apiUrl,
  schoolId,
  items,
  buyerEmail,
  canActOnIdentityVerdicts,
  timeoutMs = VALIDATE_TIMEOUT_MS,
}: ValidateArgs): Promise<BasketValidationResult[] | null> {
  const baseUrl = apiOrigin(apiUrl);

  // A request that STALLS rather than fails is the dangerous case: the drawer
  // disables Checkout while `isValidating`, so a promise that never settles
  // leaves the button dead for the rest of the page load. A captive portal, a
  // hung proxy or a sleeping mobile connection all do exactly that, and bare
  // `fetch` has no timeout of its own. Aborting turns it into the rejection
  // every caller already treats as non-blocking.
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(`${baseUrl}/widget/basket/validate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        items: buildValidatePayload(items),
        schoolId,
        ...(buyerEmail ? { buyerEmail } : {}),
      }),
      signal: controller.signal,
    });

    if (!response.ok) return null;

    const data = await response.json();
    const results = (data?.results ?? []) as BasketValidationResult[];

    // Both, not either. See `canActOnIdentityVerdicts`.
    const identityVerdictsAreUseful = !!buyerEmail && canActOnIdentityVerdicts;
    return identityVerdictsAreUseful
      ? results
      : withoutBuyerDependentVerdicts(results);
  } finally {
    clearTimeout(timeout);
  }
}
